import { useState } from "react";

/**
 * Client-side pagination for the platform-admin tables (POS2-1057, moved here by POS2-1138 CP2).
 *
 * A pure move out of `pages/admin/labelling.tsx` — no behaviour change. It lives beside
 * `components/admin/table.tsx` because the two are always used together: `bar` is exactly the props
 * that component's `<Pagination>` takes.
 */

export const ROWS_PER_PAGE = 10;

/**
 * One page of `rows`, plus the props the pagination bar needs.
 *
 * The page is clamped to the last one that exists rather than reset through an effect, so
 * a period change that shrinks a table can never leave the user looking at an empty page —
 * and there is no render where the old page is briefly out of range.
 *
 * `bar` is null when everything fits, because three bars reading "Showing 1–1 of 1" is
 * chrome carrying no information. It keys off the row count against the *default* page
 * size, not the chosen one: keying off the chosen size would hide the control that set it,
 * stranding a table on 50 rows per page with no way back.
 */
export function usePaged<T>(rows: T[]) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(ROWS_PER_PAGE);

  const lastPage = Math.max(0, Math.ceil(rows.length / pageSize) - 1);
  const current = Math.min(page, lastPage);

  return {
    page: rows.slice(current * pageSize, current * pageSize + pageSize),
    bar:
      rows.length > ROWS_PER_PAGE
        ? {
            page: current,
            pageSize,
            total: rows.length,
            onPageChange: setPage,
            onPageSizeChange: (size: number) => {
              setPageSize(size);
              setPage(0);
            },
            pageSizeOptions: [10, 25, 50],
          }
        : null,
  };
}
